import React, { useEffect, useRef } from 'react';
import { RSLabel } from 'reactsymbols-kit';
import { Bin, AddPlus, Edit } from '../Icons';
import IconButton from '../IconButton';
import { observer } from 'mobx-react';
import EditAttributeForm from './EditAttributeForm';
import Modal from '../Modal';
import AttributePopover from './AttributePopover';
import { isEmpty } from 'lodash';
import { useStores } from '../../hooks/useStores';
import type { IAttribute } from '../../stores/model/metamodel/attribute';
import type { IAttributesInspector } from '../../stores/ui/attributes_inspector';
import type Controller from '../../merode/controllers/Controller';
import { rsValue } from './rsForm';

/** The editable attribute properties this inspector reads/writes by key. */
type AttributeKey = 'name' | 'typeName' | 'description' | 'implementation' | 'validation';

/**
 * The minimal attribute surface this inspector indexes dynamically by key
 * (`attr[key]`). Covers BOTH a real `IAttribute` (string properties) and an
 * added `AttributeChanges` store entry (nullable properties) — the render path
 * handles both, so the editable keys are typed `string | null`.
 */
type AttributeRecord = Record<AttributeKey, string | null> & { id: string };

interface AttributesInspectorProps {
  /** The attributes to inspect. */
  attributes: IAttribute[];
  /** The AttributesInspector ui store. */
  ui: IAttributesInspector;
  /**
   * The controller used to get the available data types and perform name-input
   * checking. Any of the edit controllers (they all extend `Controller`).
   */
  controller: Controller;
  /** Whether to render the "Attributes" title (default true). */
  showTitle?: boolean;
}

/**
 * Attribute inspector.
 *
 * Props:
 * - attributes: array of attributes to inspect
 * - ui: the AttributeInspector store to use
 * - controller: the controller to use to get the available data types and
 *   perform name input checking
 * - showTitle: whether to render the "Attributes" title (default true)
 *
 * `isTouch` used to be injected via mobx-react; it now comes from useStores().
 */
const AttributesInspector = observer(function AttributesInspector({
  attributes,
  ui,
  controller,
  showTitle = true,
}: AttributesInspectorProps) {
  const store = useStores();
  const isTouch = store.ui.isTouch;

  /**
   * Stop editing (i.e. close modal) when we are about to die ourselves.
   *
   * Mirrors the class componentWillUnmount, which called
   * this.props.ui.discardEditing(). We keep a ref to the latest `ui` so the
   * unmount-only cleanup ([] deps) calls discardEditing on the ui that was
   * current at unmount, exactly like reading this.props.ui then.
   */
  const uiRef = useRef(ui);
  uiRef.current = ui;
  useEffect(() => {
    return () => {
      uiRef.current.discardEditing();
    };
  }, []);

  /**
   * The handlers below forward mostly to the MST store.
   */

  const onDelete = (id: string) => {
    ui.changes.remove(id);
  };

  const onUndoDelete = (id: string) => {
    ui.changes.undoRemove(id);
  };

  const onStartEdit = (id: string, adding = false) => {
    ui.startEdit(id, adding);
  };

  const onAddAttribute = () => {
    const id = ui.changes.addAttribute();
    onStartEdit(id, true);
  };

  const discardEditing = () => {
    ui.discardEditing();
  };

  const onCancelAddAttribute = () => {
    discardEditing();
    ui.changes.undoAddAttribute();
  };

  const onChangeAttribute = (
    id: string,
    key: AttributeKey,
    original: string | null,
    changed: string | null
  ) => {
    ui.changes.storeChange(id, key, original, changed);
  };

  /**
   * Get the given property from the given attribute or the corresponding change
   * in the store.
   *
   * `changeSet` is possibly-undefined, but `hasChanged` being true guarantees a
   * change set exists for this id (legacy invariant). Assert it at that branch.
   */
  const attributeProperty = (attr: AttributeRecord, key: AttributeKey): string | null => {
    return ui.changes.hasChanged(attr.id, key)
      ? ui.changes.changeSet(attr.id)![key]
      : attr[key];
  };

  /**
   * Get the configuration for the attribute type dropdown
   */
  const attributeTypeOptions = () => {
    const existing = controller.availableDataTypes;
    // `usedTypeNames` is typed `(string | null)[]` (the store view can't narrow
    // its filter), but it only ever holds the non-null type names — the store
    // filters them. Read it as `string[]` for the option mapping below.
    const usedTypeNames = ui.changes.usedTypeNames as string[];
    const newTypes = usedTypeNames.filter((tn) => !existing.includes(tn));
    return existing
      .map((dt) => ({
        label: dt,
        value: dt,
      }))
      .concat(
        newTypes.map((dt) => ({
          label: `${dt} (New)`,
          value: dt,
        }))
      );
  };

  /**
   * Render the content of an attribute.
   *
   * Only add an onClick to the wrapper if we're not on touch. Otherwise there
   * is no way to open the popover on touch devices.
   */
  const renderAttributeContent = (attr: AttributeRecord, added = false) => {
    const deleted = ui.changes.isRemoved(attr.id);
    const editing = ui.isEditing(attr.id);
    const dirty = ui.changes.isDirty(attr.id);
    // Preserve the computed state hooks (kept CSS recolours the kit
    // `.rs-label.attribute-type` pill off them) and add the app-owned layout +
    // app-state colour utilities. `.deleted` dims + strikes the whole row.
    const classNames = ['attribute', 'flex', 'items-center', 'pt-[5px]'];
    if (editing) {
      classNames.push('editing');
    } else if (deleted) {
      classNames.push('deleted', 'opacity-50', 'line-through');
    }
    if (added) {
      classNames.push('added');
    }
    if (dirty) {
      classNames.push('dirty');
    }
    // `.attribute-properties` recolours accent (dirty) / accent2 (added). The
    // SCSS `.added` block followed `.dirty` at equal specificity, so when an
    // added attribute is also dirty, accent2 wins — preserve that order here.
    const propertiesColor = added ? 'text-accent2' : dirty ? 'text-accent' : '';
    return (
      <div className={classNames.join(' ')} key={attr.id}>
        <div
          className={`attribute-properties grow cursor-pointer ${propertiesColor}`}
          onClick={isTouch ? undefined : () => onStartEdit(attr.id)}
        >
          {attributeProperty(attr, 'name')}
          <RSLabel text={rsValue(attributeProperty(attr, 'typeName'))} className="attribute-type" />
        </div>
        {deleted ? (
          <IconButton variant="inline" onClick={() => onUndoDelete(attr.id)}>
            <AddPlus />
          </IconButton>
        ) : (
          [
            <IconButton key="edit" variant="inline" onClick={() => onStartEdit(attr.id)}>
              <Edit />
            </IconButton>,
            <IconButton key="delete" variant="inline" onClick={() => onDelete(attr.id)}>
              <Bin />
            </IconButton>,
          ]
        )}
      </div>
    );
  };

  /**
   * Render an attribute including a popover if it is relevant.
   */
  const renderAttribute = (attr: AttributeRecord, added?: boolean) => {
    const popoverProperties = {
      description: attributeProperty(attr, 'description'),
      implementation: attributeProperty(attr, 'implementation'),
      validation: attributeProperty(attr, 'validation'),
    };
    if (
      !isEmpty(popoverProperties.description) ||
      !isEmpty(popoverProperties.implementation) ||
      !isEmpty(popoverProperties.validation)
    ) {
      return (
        <AttributePopover key={attr.id} preferPlace="left" attribute={popoverProperties}>
          {renderAttributeContent(attr, added)}
        </AttributePopover>
      );
    } else {
      return renderAttributeContent(attr, added);
    }
  };

  /**
   * If we're editing, render a model with an EditAttributeForm.
   */
  const renderEditAttributeForm = () => {
    if (ui.editing !== null) {
      // The editing id always resolves to an existing or added attribute here
      // (guarded by `ui.editing !== null`); both shapes expose the editable keys
      // (real attrs as `string`, added ones as `string | null`). View through the
      // shared record surface — same node at runtime.
      const attr = (attributes.find((a) => a.id === ui.editing) ||
        ui.changes.added.find((a) => a.id === ui.editing)) as unknown as AttributeRecord;
      const name = attributeProperty(attr, 'name');
      const valid = name && name.length > 0 && controller.checkNameInput(name).ok;
      return (
        <Modal
          isOpen={ui.editing !== null}
          onRequestClose={
            // if we are adding, but it is not valid -> cancel add
            // else if valid -> stop edit
            // else don't allow to close. The `null` "no handler" case is how the
            // original disabled close; react-modal's `onRequestClose` type omits
            // it, so re-type the expression to the prop shape (runtime unchanged).
            (ui.adding && !valid
              ? onCancelAddAttribute
              : valid
                ? ui.stopEdit
                : null) as React.ComponentProps<typeof Modal>['onRequestClose']
          }
        >
          <EditAttributeForm
            onRequestClose={ui.stopEdit}
            onRequestCancel={ui.adding ? onCancelAddAttribute : null}
            properties={{
              name,
              typeName: attributeProperty(attr, 'typeName'),
              description: attributeProperty(attr, 'description'),
              implementation: attributeProperty(attr, 'implementation'),
              validation: attributeProperty(attr, 'validation'),
            }}
            valid={valid}
            checkName={(n) => controller.checkNameInput(n)}
            onChange={(key, value) => onChangeAttribute(attr.id, key, attr[key], value)}
            attributeTypeOptions={attributeTypeOptions()}
          />
        </Modal>
      );
    }
  };

  const { changes } = ui;
  return (
    <div className="attributes">
      {showTitle && <div className="title inspector-subtitle">Attributes</div>}
      {(attributes.length > 0 || changes.added.length > 0) && (
        <div className="attributes-list mb-[15px]">
          {attributes.map((attr) => {
            return renderAttribute(attr);
          })}
          {changes.added.map((attr) => {
            return renderAttribute(attr, true);
          })}
        </div>
      )}
      <div className="add-attribute flex justify-center">
        <IconButton className="icon-with-label" variant="inline" onClick={() => onAddAttribute()}>
          <span>Add attribute</span>
          <AddPlus />
        </IconButton>
      </div>
      {renderEditAttributeForm()}
    </div>
  );
});

export default AttributesInspector;
